/**
 * Converts a set of user filters into a logical WHERE clause string.
 *
 * Each filter key can have multiple values (treated as an OR condition), or a single string value.
 * All different filter keys are combined as AND conditions.
 * An optional initial filter string can be appended as an additional AND condition.
 *
 * Example:
 *   filtersToWhereClause({ brand: ['Apple', 'Samsung'], color: ['Black'] }, 'status: "active"')
 *   // => '(brand: "Apple" OR brand: "Samsung") AND (color: "Black") AND status: "active"'
 *
 * @param userFilters - Filters to convert. The value can be an array of strings.
 * @param initFilter - An optional initial filter string to append.
 * @returns The combined WHERE clause string.
 */
export function filtersToWhereClause(
  userFilters: Record<string, string[]>,
  initFilter = '',
): string {
  const clauses: string[] = []

  for (const [key, values] of Object.entries(userFilters)) {
    if (Array.isArray(values) && values.length > 0) {
      // Escape and encode each value for safety and utf-8 support
      const orConditions = values
        .map((rawValue) => {
          const escaped = rawValue.replace(/"/g, '\\"')
          // Ensure utf-8
          const encoded = decodeURI(encodeURI(escaped))

          return `${key}: "${encoded}"`
        })
        .join(' OR ')
      clauses.push(`(${orConditions})`)
    }
  }

  if (initFilter) {
    clauses.push(initFilter)
  }

  // Remove empty strings and join with AND
  return clauses.filter(Boolean).join(' AND ')
}
